import type {
  InsightResult,
  PerformanceSummaryResponse,
  ReviewIntelligenceResponse,
} from "@/lib/api"
import type { OverviewPayload, ReviewTeaserPayload } from "./types"
import type {
  OverviewHouseRulesSummary,
  OverviewPropertySettingsSummary,
  ResourceStatus,
} from "./useOverviewData"
import {
  buildSinceLastVisitItems,
  extractDecisionTitle,
  formatDecimal,
  formatDateLabel,
  formatPartyLabel,
  formatPct,
  formatPctRound,
  formatRelativeTime,
  getLastSnapshot,
  normalizeStatus,
  parseDateOnlyAtMidday,
  resolveActionSurface,
  resolveCurrentPhase,
  STAGE_LABELS,
  toDateOnly,
} from "./helpers"

export type ReadinessTone = "ready" | "pending" | "warning" | "critical"
export type ActionTone = "critical" | "warning" | "opportunity"

export interface GuestCommandModel {
  stageKey: string
  stageLabel: string
  stageKicker: string
  headline: string
  summary: string
  meta: string[]
  guestName: string | null
  sourceLabel: string | null
  hasBooking: boolean
  isArrivalSoon: boolean
  isActiveStay: boolean
}

export interface OverviewReadinessItem {
  key: string
  label: string
  tone: ReadinessTone
  headline: string
  detail: string
  note?: string
  consequence: string
  surface: string
  ctaLabel: string
}

export interface OverviewActionItem {
  key: string
  tone: ActionTone
  title: string
  detail: string
  consequence: string
  surface: string
  ctaLabel: string
  sourceLabel: string
  urgency: number
  note?: string
}

export interface OverviewDeltaEntry {
  key: string
  label: string
  detail?: string
  source: "delta" | "activity"
}

function titleCase(value: unknown): string {
  if (typeof value !== "string") return ""
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function surfaceName(surface: string): string {
  if (surface === "display-behaviour") return "Display"
  if (surface === "house-rules") return "House Rules"
  return titleCase(surface)
}

function bookingSourceLabel(value: string | null | undefined): string | null {
  const source = (value || "").trim().toLowerCase()
  if (!source) return null
  if (source.includes("booking")) return "Booking.com"
  if (source.includes("airbnb")) return "Airbnb"
  if (source.includes("direct")) return "Direct"
  if (source.includes("lodgify")) return "Lodgify"
  return titleCase(source)
}

function daysUntil(isoDate: string): number | null {
  const dateOnly = toDateOnly(isoDate)
  if (!dateOnly) return null
  const today = new Date().toISOString().slice(0, 10)
  const target = parseDateOnlyAtMidday(dateOnly)
  const todayDate = parseDateOnlyAtMidday(today)
  if (!target || !todayDate) return null
  const targetMs = target.getTime()
  const todayMs = todayDate.getTime()
  return Math.round((targetMs - todayMs) / 86400000)
}

function deriveStayDay(arrivalDate: string, departureDate: string, nights: number): string | null {
  const today = new Date().toISOString().slice(0, 10)
  const arrival = toDateOnly(arrivalDate)
  const departure = toDateOnly(departureDate)
  if (!arrival || !departure || today < arrival || today >= departure) return null
  const arrivalValue = parseDateOnlyAtMidday(arrival)
  const todayValue = parseDateOnlyAtMidday(today)
  if (!arrivalValue || !todayValue) return null
  const arrivalMs = arrivalValue.getTime()
  const todayMs = todayValue.getTime()
  const day = Math.floor((todayMs - arrivalMs) / 86400000) + 1
  return `Day ${day} of ${nights}`
}

function findStatusRow(
  overview: OverviewPayload,
  keys: string[],
): NonNullable<OverviewPayload["systemStatus"]>["rows"][number] | null {
  const rows = overview.systemStatus?.rows || []
  return rows.find((row) => keys.some((key) => row.key.includes(key))) || null
}

function strongestTone(statuses: Array<string | null | undefined>): ReadinessTone {
  let hasWarning = false
  for (const status of statuses) {
    const tone = status ? normalizeStatus(status) : "neutral"
    if (tone === "error") return "critical"
    if (tone === "warning") hasWarning = true
  }
  return hasWarning ? "warning" : "ready"
}

function primarySyncNote(rows: Array<NonNullable<OverviewPayload["systemStatus"]>["rows"][number] | null>): string | undefined {
  const recentRow = rows.find((row) => row?.last_run_at) || rows.find(Boolean)
  if (!recentRow) return undefined
  if (recentRow.last_run_at) {
    return `${recentRow.label} checked ${formatRelativeTime(recentRow.last_run_at)}`
  }
  return recentRow.detail || undefined
}

export function buildGuestCommandModel(
  overview: OverviewPayload,
  propertyName: string,
): GuestCommandModel {
  const booking = overview.nextBooking
  if (!booking) {
    return {
      stageKey: "between_bookings",
      stageLabel: "Between bookings",
      stageKicker: "Between stays",
      headline: propertyName,
      summary: overview.noBookingHint || "No upcoming booking is currently on the books.",
      meta: ["Use this window to tighten the guest experience and commercial strategy."],
      guestName: null,
      sourceLabel: null,
      hasBooking: false,
      isArrivalSoon: false,
      isActiveStay: false,
    }
  }

  const stageKey = resolveCurrentPhase(booking.arrival_date, booking.departure_date) || "before_checkin"
  const sourceLabel = bookingSourceLabel(booking.source)
  const partyLabel = formatPartyLabel(booking.adults, booking.children, booking.infants)
  const stayDay = deriveStayDay(booking.arrival_date, booking.departure_date, booking.nights)
  const untilArrival = daysUntil(booking.arrival_date)
  const isActiveStay = stageKey !== "before_checkin"
  const isArrivalSoon = stageKey === "before_checkin" && untilArrival != null && untilArrival <= 3

  let stageKicker = STAGE_LABELS[stageKey] || "Upcoming stay"
  let summary = `${formatDateLabel(booking.arrival_date)} arrival · ${booking.nights} night${booking.nights !== 1 ? "s" : ""}`

  if (stageKey === "before_checkin") {
    if (untilArrival === 0) stageKicker = "Arrival today"
    else if (untilArrival === 1) stageKicker = "Arriving tomorrow"
    else if (untilArrival != null && untilArrival > 1) stageKicker = `Arrival in ${untilArrival} days`
  } else if (stageKey === "arrival_day") {
    stageKicker = "Arrival day"
  } else if (stageKey === "during_stay" && stayDay) {
    stageKicker = stayDay
  } else if (stageKey === "checkout_evening") {
    stageKicker = "Checkout tomorrow"
  } else if (stageKey === "checkout_morning") {
    stageKicker = "Checkout day"
  }

  if (stageKey === "arrival_day") {
    summary = `${booking.guest_name || "Guest"} is checking in today for ${booking.nights} night${booking.nights !== 1 ? "s" : ""}`
  } else if (stageKey === "during_stay" && stayDay) {
    summary = `${stayDay} · ${booking.nights} night${booking.nights !== 1 ? "s" : ""} stay`
  } else if (stageKey === "checkout_evening") {
    summary = `Departure prep tonight · checkout ${formatDateLabel(booking.departure_date)}`
  } else if (stageKey === "checkout_morning") {
    summary = `Departure day · staying through ${formatDateLabel(booking.departure_date)}`
  }

  const meta = [
    `${formatDateLabel(booking.arrival_date)} — ${formatDateLabel(booking.departure_date)}`,
    partyLabel,
    sourceLabel,
  ].filter(Boolean) as string[]

  return {
    stageKey,
    stageLabel: STAGE_LABELS[stageKey] || "Upcoming stay",
    stageKicker,
    headline: booking.guest_name || "Upcoming guest",
    summary,
    meta,
    guestName: booking.guest_name || null,
    sourceLabel,
    hasBooking: true,
    isArrivalSoon,
    isActiveStay,
  }
}

export function buildReadinessItems({
  overview,
  guest,
  propertySettings,
  propertySettingsStatus,
  houseRules,
  houseRulesStatus,
}: {
  overview: OverviewPayload
  guest: GuestCommandModel
  propertySettings: OverviewPropertySettingsSummary | null
  propertySettingsStatus: ResourceStatus
  houseRules: OverviewHouseRulesSummary | null
  houseRulesStatus: ResourceStatus
}): OverviewReadinessItem[] {
  const guidebookRow = findStatusRow(overview, ["guidebook_render"])
  const displayRow = findStatusRow(overview, ["display_render"])
  const researchRow = findStatusRow(overview, ["research"])
  const pmsRow = findStatusRow(overview, ["pms_sync"])
  const calendarRow = findStatusRow(overview, ["calendar_sync", "ical_sync"])
  const strategy = overview.stayStrategy?.strategy
  const guidebookAccesses = overview.systemStatus?.guidebook_accesses || []
  const guidebookAvailable = Boolean(overview.property?.guidebook_url || propertySettings?.renderKey)

  const guidebookTone: ReadinessTone = !guidebookAvailable
    ? "critical"
    : guidebookRow
      ? strongestTone([guidebookRow.status])
      : "ready"
  const guidebookNote = guidebookAccesses[0]?.created_at
    ? `Last opened ${formatRelativeTime(guidebookAccesses[0].created_at)}`
    : guidebookRow?.last_run_at
      ? `Rendered ${formatRelativeTime(guidebookRow.last_run_at)}`
      : undefined

  const displayTone: ReadinessTone = !strategy && !displayRow
    ? "pending"
    : strategy?.stale
      ? guest.isArrivalSoon || guest.isActiveStay ? "critical" : "warning"
      : displayRow
        ? strongestTone([displayRow.status])
        : "ready"
  const displayNote = strategy?.generated_at
    ? `Updated ${formatRelativeTime(strategy.generated_at)}`
    : displayRow?.last_run_at
      ? `Rendered ${formatRelativeTime(displayRow.last_run_at)}`
      : undefined

  const placesTone = researchRow
    ? strongestTone([overview.health?.places, overview.health?.research, researchRow.status])
    : overview.health?.places || overview.health?.research
      ? strongestTone([overview.health?.places, overview.health?.research])
      : "pending"

  let rulesTone: ReadinessTone = "pending"
  let rulesDetail = "Checking how rules will surface across the guest journey."
  let rulesNote: string | undefined
  if (houseRulesStatus === "failed" && !houseRules) {
    rulesTone = "warning"
    rulesDetail = "We could not verify house rules delivery just now."
  } else if (houseRules) {
    rulesNote = houseRules.updatedAt ? `Updated ${formatRelativeTime(houseRules.updatedAt)}` : houseRules.summary || undefined
    if (houseRules.enabledCoreRules >= 4 && houseRules.guidebookRules > 0) {
      rulesTone = "ready"
      rulesDetail = houseRules.displayRules > 0
        ? "Arrival, quiet hours, party, and checkout rules are set for guidebook and display delivery."
        : "Core house rules are ready for guidebook delivery."
    } else if (houseRules.enabledCoreRules >= 2) {
      rulesTone = "warning"
      rulesDetail = "Some high-value guest rules are configured, but key arrival or checkout guidance still needs review."
    } else {
      rulesTone = guest.isArrivalSoon || guest.isActiveStay ? "critical" : "warning"
      rulesDetail = "Core arrival, quiet hours, party, or checkout rules are still missing."
    }
  }

  let accessTone: ReadinessTone = "pending"
  let accessDetail = "Checking Wi-Fi, PIN, and access timing."
  let accessNote: string | undefined
  if (propertySettingsStatus === "failed" && !propertySettings) {
    accessTone = "warning"
    accessDetail = "We could not verify Wi-Fi or access essentials just now."
  } else if (propertySettings) {
    const wifiReady = Boolean(propertySettings.wifiName) && propertySettings.wifiPasswordConfigured
    const doorReady = propertySettings.doorCodeConfigured
      ? propertySettings.doorCodeShowGuidebook || propertySettings.doorCodeShowDisplay
      : false
    accessNote = propertySettings.doorCodeConfigured && propertySettings.doorCodeOffsetMinutes != null
      ? `Door code shows ${propertySettings.doorCodeOffsetMinutes} min before check-in`
      : propertySettings.guidebookPinConfigured
        ? "Guidebook PIN configured"
        : undefined

    if (wifiReady && doorReady) {
      accessTone = "ready"
      accessDetail = "Wi-Fi is saved and the door code is scheduled for guest-facing surfaces."
    } else if (wifiReady && propertySettings.doorCodeConfigured) {
      accessTone = "warning"
      accessDetail = "Wi-Fi is saved, but the door code is not surfaced anywhere guests can see it."
    } else if (wifiReady) {
      accessTone = guest.isArrivalSoon || guest.isActiveStay ? "warning" : "pending"
      accessDetail = "Wi-Fi is saved, but no door code is configured in Stayframe."
    } else if (propertySettings.doorCodeConfigured) {
      accessTone = guest.isArrivalSoon || guest.isActiveStay ? "critical" : "warning"
      accessDetail = "A door code exists, but Wi-Fi credentials are missing from property settings."
    } else {
      accessTone = guest.isArrivalSoon || guest.isActiveStay ? "critical" : "warning"
      accessDetail = "Wi-Fi and access essentials both need attention."
    }
  }

  const bookingTone = strongestTone([pmsRow?.status, calendarRow?.status])
  const bookingDetail = bookingTone === "ready"
    ? guest.hasBooking
      ? `Arrival details look current for ${formatDateLabel(overview.nextBooking?.arrival_date || "")}.`
      : "No arrival details are at risk right now."
    : guest.hasBooking
      ? `Arrival details may be stale for ${formatDateLabel(overview.nextBooking?.arrival_date || "")}'s guest.`
      : "Booking and calendar data need a refresh."

  return [
    {
      key: "guidebook",
      label: "Guidebook",
      tone: guidebookTone,
      headline: guidebookTone === "ready" ? "Guest guidebook is live" : "Guidebook needs attention",
      detail: guidebookTone === "ready"
        ? guidebookAccesses.length > 0
          ? "Guests are successfully reaching the guidebook."
          : "Your guest share flow is ready for the next arrival."
        : guidebookAvailable
          ? "Guidebook access is live, but the rendered content may not be fully current."
          : "The guidebook link is not ready to share yet.",
      note: guidebookNote,
      consequence: "Guests could miss arrival instructions, Wi-Fi, rules, or local guidance.",
      surface: "guidebook",
      ctaLabel: "Open Guidebook",
    },
    {
      key: "display",
      label: "Display",
      tone: displayTone,
      headline: displayTone === "ready" ? "In-home display is ready" : displayTone === "pending" ? "Checking display state" : "Display content may be stale",
      detail: displayTone === "ready"
        ? strategy?.generated_at
          ? "Guest-facing display content has been prepared for the current stay context."
          : "Fallback display content is available between stays."
        : displayTone === "pending"
          ? "The live preview is still checking the current display render."
          : "The display may not be reflecting the latest stay strategy or arrival details.",
      note: displayNote,
      consequence: "Arriving guests may see outdated welcomes, access prompts, or stay-phase content.",
      surface: "display-behaviour",
      ctaLabel: "Open Display",
    },
    {
      key: "places",
      label: "Places",
      tone: placesTone,
      headline: placesTone === "ready" ? "Places feel current" : placesTone === "pending" ? "Checking local place freshness" : "Places may feel dated",
      detail: placesTone === "ready"
        ? "Curated local recommendations and practical tips look ready for guest use."
        : placesTone === "pending"
          ? "Stayframe is still checking the latest place research and opening-hours coverage."
          : "Local recommendations or opening-hours intelligence may be out of date.",
      note: researchRow?.last_run_at ? `Researched ${formatRelativeTime(researchRow.last_run_at)}` : undefined,
      consequence: "Guests can lose trust quickly when opening hours or recommendations feel generic or stale.",
      surface: "places",
      ctaLabel: "Open Places",
    },
    {
      key: "rules",
      label: "Rules",
      tone: rulesTone,
      headline: rulesTone === "ready" ? "House rules are ready to surface" : rulesTone === "pending" ? "Checking rules delivery" : "Rules delivery needs attention",
      detail: rulesDetail,
      note: rulesNote,
      consequence: "Unclear rules create preventable friction during arrival, late evenings, and checkout.",
      surface: "house-rules",
      ctaLabel: "Open House Rules",
    },
    {
      key: "access",
      label: "Access",
      tone: accessTone,
      headline: accessTone === "ready" ? "Access essentials are in place" : accessTone === "pending" ? "Checking access essentials" : "Access essentials need attention",
      detail: accessDetail,
      note: accessNote,
      consequence: "Missing Wi-Fi or access instructions create the most visible first-hour guest stress.",
      surface: "settings",
      ctaLabel: "Open Settings",
    },
    {
      key: "booking",
      label: "Booking freshness",
      tone: bookingTone,
      headline: bookingTone === "ready" ? "Arrival details look current" : "Arrival details may be stale",
      detail: bookingDetail,
      note: primarySyncNote([pmsRow, calendarRow]),
      consequence: "If booking data drifts, your guidebook, rules, and access timing all lose accuracy.",
      surface: "settings",
      ctaLabel: "Check Sync",
    },
  ]
}

export function buildOverviewActions({
  readinessItems,
  insights,
  plan,
}: {
  readinessItems: OverviewReadinessItem[]
  insights: InsightResult | null
  plan?: string
}): OverviewActionItem[] {
  const readinessActions = readinessItems
    .filter((item) => item.tone === "warning" || item.tone === "critical")
    .map<OverviewActionItem>((item) => ({
      key: `readiness-${item.key}`,
      tone: item.tone === "critical" ? "critical" : "warning",
      title: item.headline,
      detail: item.detail,
      consequence: item.consequence,
      surface: item.surface,
      ctaLabel: item.ctaLabel,
      sourceLabel: "Stay readiness",
      urgency: item.tone === "critical" ? 100 : 78,
      note: item.note,
    }))

  const aiActions = plan === "host"
    ? []
    : (insights?.recommendations || []).slice(0, 3).map<OverviewActionItem>((recommendation, index) => {
      const { title, detail } = extractDecisionTitle(recommendation.action)
      const surface = resolveActionSurface(recommendation.action)
      const consequence = recommendation.metric_target
        ? `Business target: ${recommendation.metric_target}.`
        : "This affects how hard the property is working commercially right now."
      return {
        key: `ai-${index}-${surface}`,
        tone: recommendation.priority === "act_now" ? "opportunity" : "warning",
        title,
        detail,
        consequence,
        surface,
        ctaLabel: `Open ${surfaceName(surface)}`,
        sourceLabel: "AI decisioning",
        urgency: recommendation.priority === "act_now" ? 64 : 42,
        note: `${titleCase(recommendation.confidence)} confidence`,
      }
    })

  return [...readinessActions, ...aiActions]
    .sort((a, b) => b.urgency - a.urgency)
    .slice(0, 6)
}

export function buildSinceLastVisitModel({
  propertyId,
  overview,
  reviewTeaser,
  performanceSummary,
}: {
  propertyId: string
  overview: OverviewPayload
  reviewTeaser?: ReviewTeaserPayload
  performanceSummary?: PerformanceSummaryResponse | null
}): {
  snapshotLabel: string | null
  items: OverviewDeltaEntry[]
} {
  const snapshot = getLastSnapshot(propertyId)
  const deltaItems = buildSinceLastVisitItems(propertyId, overview, reviewTeaser, performanceSummary || undefined)
  const items = deltaItems.length > 0
    ? deltaItems.map<OverviewDeltaEntry>((item, index) => ({
      key: `delta-${item.kind}-${index}`,
      label: item.label,
      detail: item.detail,
      source: "delta",
    }))
    : (overview.recentActivity || []).slice(0, 4).map<OverviewDeltaEntry>((item) => ({
      key: `activity-${item.id}`,
      label: item.label,
      detail: formatRelativeTime(item.timestamp),
      source: "activity",
    }))

  return {
    snapshotLabel: snapshot?.timestamp ? `Last visit ${formatRelativeTime(snapshot.timestamp)}` : null,
    items,
  }
}

export function buildCommercialNarrative(
  performanceSummary: PerformanceSummaryResponse | null,
  insights: InsightResult | null,
): string {
  const aiSummary = insights?.human_summary?.what_matters_now?.[0]
  if (typeof aiSummary === "string" && aiSummary.trim()) return aiSummary

  const pace = performanceSummary?.pacing
  if (pace?.delta_pct != null) {
    return `Booked pace is ${formatPct(pace.delta_pct)} versus the same period last year.`
  }

  const topChannel = performanceSummary?.channels?.[0]
  if (topChannel) {
    return `${topChannel.name} currently drives ${formatPctRound(topChannel.pct)} of booked revenue.`
  }

  return "Commercial signals will sharpen as more bookings and pacing data come in."
}

export function buildReputationNarrative(
  reviewIntelligence: ReviewIntelligenceResponse | null,
  reviewTeaser?: ReviewTeaserPayload,
): string {
  if (typeof reviewIntelligence?.analysis?.identity === "string" && reviewIntelligence.analysis.identity.trim()) return reviewIntelligence.analysis.identity
  if (typeof reviewIntelligence?.analysis?.teaser_sentence === "string" && reviewIntelligence.analysis.teaser_sentence.trim()) return reviewIntelligence.analysis.teaser_sentence
  if (typeof reviewTeaser?.teaser_sentence === "string" && reviewTeaser.teaser_sentence.trim()) return reviewTeaser.teaser_sentence
  if (reviewTeaser?.average_rating != null) {
    return `Average guest rating sits at ${formatDecimal(reviewTeaser.average_rating)} across ${reviewTeaser.total_reviews_count} reviews.`
  }
  return "Reputation signals will appear here once review data is synced."
}
